import { Controller } from "@hotwired/stimulus"
import { prefersReducedMotion } from "../lib/motion"

/**
 * Category Rotator Controller
 * Animates rotating category/district names with slide-up animation inside search bar placeholder
 *
 * Features:
 * - Slide-up animation on category change
 * - Pause on hover/focus for accessibility
 * - Respects prefers-reduced-motion
 * - aria-live="polite" for screen readers
 * - Hides placeholder when input is focused or has value
 * - Supports external data via Stimulus dispatch (e.g., from district-autocomplete)
 *
 * Usage with static categories:
 * <div data-controller="category-rotator"
 *      data-category-rotator-categories-value='["電子", "家具", "服飾"]'
 *      data-category-rotator-interval-value="3000">
 *
 * Usage with external data (via Stimulus dispatch):
 * <div data-controller="district-autocomplete category-rotator"
 *      data-category-rotator-interval-value="3000">
 * The district-autocomplete controller dispatches "trendingLoaded" with { names: [...] }
 */
export default class extends Controller {
  static targets = ["slot", "placeholder", "input"]
  static values = {
    categories: { type: Array, default: [] },
    interval: { type: Number, default: 3000 }
  }

  connect() {
    this.currentIndex = 0
    this.isAnimating = false
    this.isPaused = false
    this.initialized = false // Prevent duplicate initialization

    // Check for reduced motion preference
    this.prefersReducedMotion = prefersReducedMotion()

    // Pause on hover/focus for accessibility
    this.element.addEventListener("mouseenter", this.pause)
    this.element.addEventListener("mouseleave", this.resume)
    this.element.addEventListener("focusin", this.pause)
    this.element.addEventListener("focusout", this.resume)

    // Listen for external data from sibling controllers (e.g., district-autocomplete)
    // Stimulus dispatch format: "controllerIdentifier:eventName"
    this.element.addEventListener("district-autocomplete:trendingLoaded", this.handleExternalData)

    // Initialize placeholder visibility
    this.updatePlaceholderVisibility()

    // Add input event listeners if input target exists
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("focus", this.hidePlaceholder)
      this.inputTarget.addEventListener("blur", this.showPlaceholderIfEmpty)
      this.inputTarget.addEventListener("input", this.updatePlaceholderVisibility)
      this.inputTarget.addEventListener("autocomplete:selected", this.updatePlaceholderVisibility)
    }

    // Initialize with static categories if available
    if (this.categoriesValue.length > 0) {
      this.initializeRotation()
    }
  }

  disconnect() {
    this.stopRotation()
    this.element.removeEventListener("mouseenter", this.pause)
    this.element.removeEventListener("mouseleave", this.resume)
    this.element.removeEventListener("focusin", this.pause)
    this.element.removeEventListener("focusout", this.resume)
    this.element.removeEventListener("district-autocomplete:trendingLoaded", this.handleExternalData)

    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("focus", this.hidePlaceholder)
      this.inputTarget.removeEventListener("blur", this.showPlaceholderIfEmpty)
      this.inputTarget.removeEventListener("input", this.updatePlaceholderVisibility)
      this.inputTarget.removeEventListener("autocomplete:selected", this.updatePlaceholderVisibility)
    }
  }

  /**
   * Handle external data from Stimulus dispatch (e.g., district-autocomplete:trendingLoaded)
   * @param {CustomEvent} event - Event with { detail: { names: string[] } }
   */
  handleExternalData = (event) => {
    const names = event.detail?.names
    if (Array.isArray(names) && names.length > 0) {
      this.categoriesValue = names
      this.initializeRotation()
    }
  }

  /**
   * Initialize rotation with current categories
   * Prevents duplicate initialization
   */
  initializeRotation() {
    // Don't re-initialize if already running
    if (this.initialized && this.rotationTimer) return
    this.initialized = true

    // Show first category immediately
    if (this.categoriesValue.length > 0) {
      this.showCategory(this.categoriesValue[0])
    }

    // Start rotation if multiple categories
    if (this.categoriesValue.length > 1) {
      this.stopRotation() // Clear any existing timer
      this.startRotation()
    }
  }

  startRotation() {
    this.rotationTimer = setInterval(() => {
      if (!this.isPaused && !this.isAnimating) {
        this.rotateToNext()
      }
    }, this.intervalValue)
  }

  stopRotation() {
    if (this.rotationTimer) {
      clearInterval(this.rotationTimer)
      this.rotationTimer = null
    }
  }

  pause = () => {
    this.isPaused = true
  }

  resume = () => {
    this.isPaused = false
  }

  hidePlaceholder = () => {
    if (this.hasPlaceholderTarget) {
      this.placeholderTarget.classList.add("opacity-0")
    }
  }

  showPlaceholderIfEmpty = () => {
    if (this.hasPlaceholderTarget && this.hasInputTarget) {
      if (!this.inputTarget.value) {
        this.placeholderTarget.classList.remove("opacity-0")
      }
    }
  }

  updatePlaceholderVisibility = () => {
    if (this.hasPlaceholderTarget && this.hasInputTarget) {
      if (this.inputTarget.value || document.activeElement === this.inputTarget) {
        this.placeholderTarget.classList.add("opacity-0")
      } else {
        this.placeholderTarget.classList.remove("opacity-0")
      }
    }
  }

  rotateToNext() {
    const nextIndex = (this.currentIndex + 1) % this.categoriesValue.length
    const nextCategory = this.categoriesValue[nextIndex]

    if (this.prefersReducedMotion) {
      // No animation for reduced motion preference
      this.showCategory(nextCategory)
      this.currentIndex = nextIndex
    } else {
      this.animateTransition(nextCategory, nextIndex)
    }
  }

  showCategory(category) {
    if (this.hasSlotTarget) {
      this.slotTarget.textContent = category
    }
  }

  animateTransition(nextCategory, nextIndex) {
    if (this.isAnimating || !this.hasSlotTarget) return

    this.isAnimating = true
    const slot = this.slotTarget

    // Slide out current category
    slot.classList.remove("animate-category-in")
    slot.classList.add("animate-category-out")

    // After slide-out animation completes, change text and slide in
    setTimeout(() => {
      slot.textContent = nextCategory
      slot.classList.remove("animate-category-out")
      slot.classList.add("animate-category-in")

      this.currentIndex = nextIndex

      // Animation complete
      setTimeout(() => {
        this.isAnimating = false
      }, 250) // Match slide-up-in duration
    }, 200) // Match slide-up-out duration
  }
}
